/* eslint-disable max-lines */
import { captureException } from '@sentry/browser'
import type { DocumentData, DocumentReference } from 'firebase/firestore'
import { getDocs, increment, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore'
import { getIdFromRef } from '../helpers/getIdFromRef'
import {
  getCutleryTotal,
  getOrdersTotal,
  getPaperBagTotal,
  getTotalWithSalesPercentage,
  isApproved,
} from '../helpers/orders'
import { getSingleOrdersStatistic } from '../firestoreWrappers/firestoreWrappers'
import { mapTimeFromFirebaseFormat, toDayjsDate } from '../helpers/time'
import { dataRange } from '../helpers/range'
import { errorDefault } from '../helpers/snackbar'
import { isManagerOrDeliverOrOwner } from '../helpers/roles'
import { mapDateToFirebaseFormat } from '../helpers/timeFirebase'
import { defineStore } from 'pinia'
import OrderModel from '~/models/OrderModel'
import { useRouteStore } from './routes'
import {
  ordersDate,
  ordersFixedSales,
  ordersIsNewUser,
  ordersLoyaltyProgram,
  ordersOnlinePayment,
  ordersSalesPercentage,
  ordersStatistic,
  ordersSubsidy as ordersSubsidyQuery,
} from '~/firestoreWrappers/orders'
import { useSuppliersStore } from './suppliers'
import { reactive, toRefs } from 'vue'
import { useSharedStore } from './shared'
import { useSnackbarStore } from './snackbar'

const statusOrder = {
  approved: 'approved',
  pending: 'pending',
  rejected: 'rejected',
}

const FirebaseFunctionsNames = {
  getMoneyForOrder: '/getMoneyForOrder',
  orderStatusChanged: '/orderStatusChanged',
  refundMoney: '/refundMoney',
}

function onErrorShowSnackbar(error: unknown) {
  useSnackbarStore().showSnackbar(errorDefault(error),
  )
}

function getCollection(collection: any) {
  return isManagerOrDeliverOrOwner(collection)
    ? `${collection}s`
    : ''
}

function removeUnusedKeysFromOrders(productIndex: number, newStatus: string) {
  return ({
    _loading,
    'index': orderIndex,
    ...item
  }: {
    _loading: boolean
    index: number
    [key: string]: any
  }) => (orderIndex === productIndex
    ? {
        ...item,
        status: newStatus,
      }
    : item)
}

const MASTER_BRANCH = 'master'
const LEGACY_BRANCH = 'legacy'
const DEV_BRANCH = 'develop'
const E_CATERING_BRANCH = 'eCateringPanel'
const FIX_MASTER_BRANCH = 'fix-master'

// @ts-expect-error
const isProduction = import.meta.env.VITE_CI_COMMIT_REF_NAME === MASTER_BRANCH
// @ts-expect-error
  || import.meta.env.VITE_CI_COMMIT_REF_NAME === FIX_MASTER_BRANCH
  // @ts-expect-error
  || import.meta.env.VITE_CI_COMMIT_REF_NAME === LEGACY_BRANCH
  // @ts-expect-error
  || import.meta.env.VITE_CI_COMMIT_REF_NAME === DEV_BRANCH
  // @ts-expect-error
  || import.meta.env.VITE_CI_COMMIT_REF_NAME === E_CATERING_BRANCH

const baseURLProduction = 'https://europe-west1-good-dystrybucja.cloudfunctions.net'
const baseURLStaging = 'https://europe-west1-good-dystrybucja.cloudfunctions.net'

const baseURL = isProduction
  ? baseURLProduction
  : baseURLStaging

const HEADERS = {
  'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
  'Access-Control-Allow-Methods': 'POST',
  'Access-Control-Allow-Origin': '*',
  'Content-Type': 'application/json',
  'Accept': 'application/json, text/plain, */*',
}

const removeEmpty = (obj: any) => Object.keys(obj).forEach(key => obj[key] == null && delete obj[key])

function sentNotificationOrderStatusChanged(orderStatus: any, userData: any, company: any, route: any, order: any, additionalData = {}) {
  const callData = {
    deliverName: route.isSubDeliver
      ? `${route.subDeliverFirstName} ${route.subDeliverLastName}`
      : `${route.deliverFirstName} ${route.deliverLastName}`,
    orderStatus,
    title: company.name,
    userRef: getIdFromRef(order.user),
    ...additionalData,
  }

  removeEmpty(callData)

  fetch(
    baseURL + FirebaseFunctionsNames.orderStatusChanged,
    {
      method: 'POST',
      headers: {
        ...HEADERS,
        collection: getCollection(userData.role),
        uid: userData.reference.id,
      },
      body: JSON.stringify(callData),
    },
  ).catch(onErrorShowSnackbar)
}

function sentNotificationGetMoneyForOrder(userData: any, company: any, order: any, price: any, suppliers: any, onFinally = () => {}) {
  const supplier = suppliers.filter(({ 'reference': { id } }: { reference: DocumentReference }) => id === order.supplier.id)

  const getSubMerchantId = (companyData: any) => (companyData?.subMerchantIds && companyData?.subMerchantIds[supplier?.place?.city]
    ? companyData?.subMerchantIds[supplier?.place?.city]
    : companyData?.subMerchantId)

  const approvedOrders = order.orders.filter(isApproved)

  const orderTotal = getOrdersTotal(approvedOrders)
  const paperBag = getPaperBagTotal(order)
  const cutleryTotal = getCutleryTotal(order)

  const total = getTotalWithSalesPercentage(
    order,
    orderTotal + paperBag + cutleryTotal - price,
  )

  const callData = {
    amount: total,
    authorizationId: order.paymentId?.authorizationId,
    description: `Zamówienie Nr ${order.orderNumber}`,
    orderRef: order.reference.id,
    saleId: order.paymentId.saleId,
    subMerchantFee: company?.subMerchantFee,
    subMerchantId: getSubMerchantId(company),
    userRef: getIdFromRef(order.user),
  }

  removeEmpty(callData)

  fetch(
    baseURL + FirebaseFunctionsNames.getMoneyForOrder,
    {
      method: 'POST',
      headers: {
        ...HEADERS,
        collection: getCollection(userData.role),
        uid: userData.reference.id,
      },
      body: JSON.stringify(callData),
    },
  )
    .catch(onErrorShowSnackbar)
    .finally(onFinally)
}

function sentNotificationRefundMoney(userData: any, company: any, order: any, price: any, suppliers: any, isCurrentDate: any, onFinally = () => {}) {
  const supplier = suppliers.find(({ 'reference': { id } }: { reference: DocumentReference }) => id === order.supplier.id)
  const getSubMerchant = (companyData: any) => (companyData?.subMerchantIds && companyData?.subMerchantIds[supplier?.place?.city]
    ? companyData?.subMerchantIds[supplier?.place?.city]
    : companyData?.subMerchantId)

  const approvedOrders = order.orders.filter(isApproved)
  const roundResult = (sum = 0) => Math.round(sum * 100) / 100

  const getTotalWithSalesPercentageNew = (orderTmp: any, sum: any) => {
    if (orderTmp.salesPercentage && orderTmp.salesPercentage !== 0)
      return Math.round(sum * (1 - orderTmp.salesPercentage) * 100) / 100
    else if (orderTmp.fixedSalesAmount && orderTmp.fixedSalesAmount !== 0)
      return Math.round(sum - orderTmp.fixedSalesAmount)

    return roundResult(sum)
  }

  const orderTotal = getOrdersTotal(approvedOrders)
  const paperBag = getPaperBagTotal(approvedOrders)
  const cutleryTotal = getCutleryTotal(order)

  const total = getTotalWithSalesPercentageNew(
    order,
    orderTotal + paperBag + cutleryTotal - price,
  )

  const callData = {
    amount: total,
    description: `Zamówienie Nr ${order.orderNumber}`,
    orderRef: order.reference.id,
    saleId: order.paymentId.saleId,
    subMerchantId: getSubMerchant(company),
    userRef: getIdFromRef(order.user),
  }

  removeEmpty(callData)

  fetch(
    baseURL + FirebaseFunctionsNames.refundMoney,
    {
      method: 'POST',
      headers: {
        ...HEADERS,
        collection: getCollection(userData.role),
        uid: userData.reference.id,
      },
      body: JSON.stringify(callData),
    },
  )
    .catch(onErrorShowSnackbar)
    .then(async () => {
      if (!isCurrentDate) {
        try {
          let newDate
          const orderDate = toDayjsDate(order.date)
          const tomorrowOrderTime = toDayjsDate(company.tomorrowOrderTime)
          if (
            orderDate.hour() > tomorrowOrderTime.hour()
            || (orderDate.hour() === tomorrowOrderTime.hour() && orderDate.minute() > tomorrowOrderTime.minute())
          ) {
            const days = [
              'sunday',
              'monday',
              'tuesday',
              'wednesday',
              'thursday',
              'friday',
              'saturday',
            ]

            newDate = toDayjsDate(order.date).hour(24).minute(0).second(0).millisecond(0)

            while (!company.workingDays.includes(days[newDate.day() % 7])) {
              newDate = newDate.add(
                1,
                'day',
              )
            }
          }
          else {
            newDate = toDayjsDate(order.date).hour(0).minute(0).second(0).millisecond(0)
          }

          const response = await getDocs(getSingleOrdersStatistic(
            company.reference,
            supplier.place.city,
            mapDateToFirebaseFormat(newDate.toDate()),
          ))

          if (!response.empty) {
            if (order.paymentStatus === 'partialRefund') {
              const data = {
                onlineTransactionReports: {
                  ...response.docs[0].data().onlineTransactionReports,
                },
              }
              data.onlineTransactionReports.partialRefundTransactions -= total
              data.onlineTransactionReports.partialRefundTransactionsCount -= 1
              data.onlineTransactionReports.refundTransactions += total
              data.onlineTransactionReports.refundTransactionsCount += 1
              await updateDoc(
                response.docs[0].ref,
                data,
              )
            }
            else {
              const data = {
                onlineTransactionReports: {
                  ...response.docs[0].data().onlineTransactionReports,
                },
              }
              data.onlineTransactionReports.successTransactions -= total
              data.onlineTransactionReports.successTransactionsCount -= 1
              data.onlineTransactionReports.refundTransactions += total
              data.onlineTransactionReports.refundTransactionsCount += 1

              await updateDoc(
                response.docs[0].ref,
                data,
              )
            }
          }
        }
        catch (error: unknown) {
          captureException(error)
        }
      }
    })
    .finally(onFinally)
}

function onFinallySharedLoading() {
  useSharedStore().setLoading(false,
  )
}

function updateProduct(index: any, key: any, value: any) {
  return (product: any) => {
    if (product.index === index)
      product[key] = value

    return product
  }
}

function initialState() {
  return {
    orders: null,
    ordersSubsidy: null,
    ordersSubsidyLoading: null,
    ordersSubsidyError: null,
    statisticOrders: null,
    unsubscribe: null,

    ordersOnlinePeyment: null,
    ordersOnlinePeymentLoading: null,
    ordersOnlinePeymentError: null,

    ordersSalesPercentage: null,
    ordersSalesPercentageLoading: null,
    ordersSalesPercentageError: null,

    ordersFixedSales: null,
    ordersFixedSalesLoading: null,
    ordersFixedSalesError: null,

    ordersLoyaltyProgram: null,
    ordersLoyaltyProgramLoading: null,
    ordersLoyaltyProgramError: null,

    ordersIsNewUser: null,
    ordersIsNewUserLoading: null,
    ordersIsNewUserError: null,
  }
}

export const PAYMENT_METHODS_SUBSIDY = [
  'subsidy',
  'subsidyGooglePay',
  'subsidyBlik',
  'subsidyCardOnline',
  'subsidyCash',
  'subsidyCardOnDelivery',
  'subsidyBankTransfers',
]

export function mapOrders(data: DocumentData) {
  return new OrderModel(
    data.data(),
    data.ref,
  )
}

export const Order = {
  paymentStatus: {
    success: 'success',
    waiting: 'waiting',
    failed: 'failed',
    refund: 'refund',
    partialRefund: 'partialRefund',
  },
  completementStatus: {
    waitingFirmAccept: 'waitingFirmAccept',
    waitingCustomerAccept: 'waitingCustomerAccept',
    completed: 'completed',
    cancelled: 'cancelled',
    rejected: 'rejected',
    failed: 'failed',
  },
  orderStatus: {
    waitingPackaging: 'waitingPackaging',
    packaged: 'packaged',
    delivered: 'delivered',
  },
  paymentMethod: {
    cardOnline: 'cardOnline',
    cardOnDelivery: 'cardOnDelivery',
    cash: 'cash',
    blik: 'blik',
    bankTransfers: 'bankTransfers',
  },
}

const ordersModule = {
  namespaced: true,
  state: initialState(),
  actions: {
  // @ts-expect-error
    resetState({ commit, 'state': { unsubscribe: stateUnsubscribe } }) {
      if (stateUnsubscribe)
        stateUnsubscribe()

      commit('RESET_STATE')
    },
    // @ts-expect-error
    getOrders({ commit, 'state': { 'orders': stateOrdersList, unsubscribe: stateUnsubscribe } }, { startDateFirebase, endDateFirebase, route, dateChanged = false, userData }) {
      const company = userData?.company
      if (!company)
        return

      const routeId = getIdFromRef(route.reference)
      if (stateOrdersList === null || !stateOrdersList[routeId] || dateChanged) {
        if (stateUnsubscribe) {
          stateUnsubscribe()
          commit(
            'SAVE_UNSUBSCRIBE',
            null,
          )
        }

        const onErrorOnSnapshot = (error: unknown) => {
          if ((error as any).code !== 'permission-denied') {
            useSharedStore().errorAction(error,
            )
            useSnackbarStore().showSnackbar(errorDefault(error),
            )
          }
        }

        const onSuccess = (arg0: DocumentData) => {
          commit(
            'SET_ORDERS',
            {
              ...stateOrdersList,
              [routeId]: arg0.docs.map(mapOrders),
            },
          )
          onFinallySharedLoading()
        }

        useSharedStore().init()
        const unsubscribeNew = onSnapshot(
          ordersDate(
            company,
            startDateFirebase,
            endDateFirebase,
            route.reference,
          ),
          onSuccess,
          onErrorOnSnapshot,
        )

        commit(
          'SAVE_UNSUBSCRIBE',
          unsubscribeNew,
        )
      }
      else {
        commit(
          'SET_ORDERS',
          {
            ...stateOrdersList,
          },
        )
      }
    },
    getOrdersSubsidy(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, suppliers, userData },
    ) {
      useSharedStore().init()
      commit('GET_ORDERS_SUBSIDY_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        commit(
          'GET_ORDERS_SUBSIDY_ERROR',
          error,
        )
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (data: any) => {
        const result = data.map((arg0: DocumentData) => arg0.docs).flat()
        commit(
          'GET_ORDERS_SUBSIDY_SUCCESS',
          {
            city,
            date,
            orders: result.map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      // @ts-expect-error
      const suppliersPromises = suppliers.map(supplier => getDocs(ordersSubsidyQuery(
        userData.company,
        startDate,
        endDate,
        supplier.reference,
      )))
      Promise.all(suppliersPromises).then(onSuccess).catch(onError)
    },
    getOrdersOnlinePayment(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, userData },
    ) {
      useSharedStore().init()

      commit('GET_ORDERS_ONLINE_PEYMENT_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        commit(
          'GET_ORDERS_ONLINE_PEYMENT_ERROR',
          error,
        )
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (arg0: DocumentData) => {
        commit(
          'GET_ORDERS_ONLINE_PEYMENT_SUCCESS',
          {
            city,
            date,
            orders: arg0.docs.map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      getDocs(ordersOnlinePayment(
        userData.company,
        startDate,
        endDate,
      )).then(onSuccess).catch(onError)
    },

    getOrdersFixedSales(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, userData },
    ) {
      useSharedStore().init()

      commit('GET_ORDERS_SALES_FIXED_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (requestData: any) => {
        commit(
          'GET_ORDERS_SALES_FIXED_SUCCESS',
          {
            city,
            date,
            orders: requestData.docs.map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      const promiseOrdersFixedSales = getDocs(ordersFixedSales(
        userData.company,
        startDate,
        endDate,
      ))

      promiseOrdersFixedSales.then(onSuccess).catch(onError)
    },
    getOrdersSalesPercentage(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, userData },
    ) {
      useSharedStore().init()

      commit('GET_ORDERS_SALES_PERCENTAGE_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        commit(
          'GET_ORDERS_SALES_PERCENTAGE_ERRROR',
          error,
        )
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (requestData: any) => {
        commit(
          'GET_ORDERS_SALES_PERCENTAGE_SUCCESS',
          {
            city,
            date,
            orders: requestData
            // @ts-expect-error
              .map(item => item.docs)
              .flat()
              .map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      const promiseordersSalesPercentage = dataRange.map(item => getDocs(ordersSalesPercentage(
        userData.company,
        startDate,
        endDate,
        item,
      )))

      Promise.all(promiseordersSalesPercentage).then(onSuccess).catch(onError)
    },

    getOrdersLoyaltyProgram(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, userData },
    ) {
      useSharedStore().init()

      commit('GET_ORDERS_LOYALTY_PROGRAM_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        commit(
          'GET_ORDERS_LOYALTY_PROGRAM_ERRROR',
          error,
        )
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (arg0: DocumentData) => {
        commit(
          'GET_ORDERS_LOYALTY_PROGRAM_SUCCESS',
          {
            city,
            date,
            orders: arg0.docs.map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      getDocs(ordersLoyaltyProgram(
        userData.company,
        startDate,
        endDate,
      )).then(onSuccess).catch(onError)
    },

    getOrdersIsNewUser(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { city, date, startDate, endDate, userData },
    ) {
      useSharedStore().init()

      commit('GET_ORDERS_IS_NEW_USER_REQUEST')

      const onError = (error: unknown) => {
        console.error(error)
        commit(
          'GET_ORDERS_IS_NEW_USER_ERRROR',
          error,
        )
        useSharedStore().errorAction(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      const onSuccess = (arg0: DocumentData) => {
        commit(
          'GET_ORDERS_IS_NEW_USER_SUCCESS',
          {
            city,
            date,
            orders: arg0.docs.map(mapOrders),
          },
        )
        onFinallySharedLoading()
      }

      getDocs(ordersIsNewUser(
        userData.company,
        startDate,
        endDate,
      )).then(onSuccess).catch(onError)
    },

    async changeRouteOrdersCount(
      // @ts-expect-error
      _any,
      // @ts-expect-error
      { route, ordersCount, userData },
    ) {
      await updateDoc(
        route.reference,
        {
          ordersCount: Number(ordersCount),
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
    },

    async changeRoutePendingOrdersCount(
      // @ts-expect-error
      _any,
      // @ts-expect-error
      { route, pendingOrders, userData },
    ) {
      await updateDoc(
        route.reference,
        {
          pendingOrders: Number(pendingOrders),
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
    },
    getStatisticOrders(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { startDateFirebase, endDateFirebase, userData },
    ) {
      useSharedStore().init()

      const onErrorOnSnapshot = (error: unknown) => {
        if ((error as any).code !== 'permission-denied') {
          useSharedStore().errorAction(error,
          )
          useSnackbarStore().showSnackbar(errorDefault(error),
          )
        }
      }

      const onSuccess = (arg0: DocumentData) => {
        commit(
          'SET_STATISTIC_ORDERS',
          arg0.docs.map(mapOrders),
        )
        onFinallySharedLoading()
      }

      getDocs(ordersStatistic(
        userData.company,
        startDateFirebase,
        endDateFirebase,
      )).then(onSuccess).catch(onErrorOnSnapshot)
    },
    approveProducts(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, index, userData },
    ) {
      commit(
        'SET_LOADING_ORDER',
        {
          order,
          index,
          loading: true,
        },
      )

      const onFinally = () => {
        commit(
          'SET_LOADING_ORDER',
          {
            order,
            index,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          orders: order.orders.map(removeUnusedKeysFromOrders(
            index,
            statusOrder.approved,
          )),
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    rejectProducts(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, index, userData },
    ) {
      commit(
        'SET_LOADING_ORDER',
        {
          order,
          index,
          loading: true,
        },
      )

      const onFinally = () => {
        commit(
          'SET_LOADING_ORDER',
          {
            order,
            index,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          orders: order.orders.map(removeUnusedKeysFromOrders(
            index,
            statusOrder.rejected,
          )),
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    approveWholeOrder(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, route, company, userData },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onSuccessCompleted = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )
        useSuppliersStore().updateOrder(
          order.supplier,
          -1,
          1,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'accepted',
          userData,
          company,
          route,
          order,
        )
      }

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.completed,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccessCompleted)
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    approveWholeOrderAndGetPayment(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, route, company, price, userData, suppliers },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onSuccess = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )

        useSuppliersStore().updateOrder(
          order.supplier,
          -1,
          1,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'accepted',
          userData,
          company,
          route,
          order,
        )

        sentNotificationGetMoneyForOrder(
          userData,
          company,
          order,
          price,
          suppliers,
        )
      }

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.completed,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccess)
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    approveWholeOrderAndDeliveryTime(
      // @ts-expect-error
      _any,
      // @ts-expect-error
      { order, route, company, deliveryTime, time, userData },
    ) {
      useSharedStore().init()

      const onSuccessCompleted = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )

        useSuppliersStore().updateOrder(
          order.supplier,
          -1,
          1,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'accepted',
          userData,
          company,
          route,
          order,
          { time },
        )
      }

      const onError = (error: unknown) => {
        console.error(error)
        useSharedStore().setError(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.completed,
          deliveryTime,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccessCompleted)
        .catch(onError)
        .finally(onFinallySharedLoading)
    },
    changeTime(
      // @ts-expect-error
      _any,
      // @ts-expect-error
      { order, route, company, deliveryTime, userData },
    ) {
      useSharedStore().init()

      const onSuccessCompleted = () => {
        sentNotificationOrderStatusChanged(
          'deliveryTimeChanged',
          userData,
          company,
          route,
          order,
          {
            orderStatus: order.deliveryTime
              ? 'deliveryTimeChanged'
              : 'deliveryTimeSet',
            deliveryTime: mapTimeFromFirebaseFormat(deliveryTime),
          },
        )
      }

      const onError = (error: unknown) => {
        console.error(error)
        useSharedStore().setError(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      updateDoc(
        order.reference,
        {
          deliveryTime,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccessCompleted)
        .catch(onError)
        .finally(onFinallySharedLoading)
    },
    approveWholeOrderAndDeliveryTimeAndGetPayment(
      // @ts-expect-error
      _any,
      // @ts-expect-error
      { order, route, company, deliveryTime, time, price, userData, suppliers },
    ) {
      useSharedStore().init()

      const onSuccess = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )

        useSuppliersStore().updateOrder(
          order.supplier,
          -1,
          1,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'accepted',
          userData,
          company,
          route,
          order,
          { time },
        )

        sentNotificationGetMoneyForOrder(
          userData,
          company,
          order,
          price,
          suppliers,
        )
      }

      const onError = (error: unknown) => {
        console.error(error)
        useSharedStore().setError(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.completed,
          deliveryTime,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccess)
        .catch(onError)
        .finally(onFinallySharedLoading)
    },
    approveWholeOrderWithMessage(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, route, company, messages, userData },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onSuccess = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )

        useSuppliersStore().updateOrder(
          order.supplier,
          0,
          1,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'rejected',
          userData,
          company,
          route,
          order,
          {
            message: messages.message,
            messageEN: messages.messageEN,
          },
        )
      }

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.waitingCustomerAccept,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccess)
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    getPayment(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, company, price, userData, suppliers },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      sentNotificationGetMoneyForOrder(
        userData,
        company,
        order,
        price,
        suppliers,
        onFinally,
      )
    },
    refundMoney(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, company, price, isCurrentDate, userData, suppliers },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      sentNotificationRefundMoney(
        userData,
        company,
        order,
        price,
        suppliers,
        isCurrentDate,
        onFinally,
      )
    },
    rejectWholeOrder(
      {
        // @ts-expect-error
        commit,
      },
      // @ts-expect-error
      { order, route, company, userData },
    ) {
      commit(
        'SET_LOADING_WHOLE_ORDER',
        {
          order,
          loading: true,
        },
      )

      const onSuccess = () => {
        useRouteStore().decreasePendingOrders(
          { userData, route },
        )

        useSuppliersStore().updateOrder(
          order.supplier,
          -1,
          0,
          userData,
        )

        sentNotificationOrderStatusChanged(
          'discard',
          userData,
          company,
          route,
          order,
        )
      }

      const onFinally = () => {
        commit(
          'SET_LOADING_WHOLE_ORDER',
          {
            order,
            loading: false,
          },
        )
      }

      updateDoc(
        order.reference,
        {
          completementStatus: Order.completementStatus.rejected,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccess)
        .catch(onErrorShowSnackbar)
        .finally(onFinally)
    },
    moveOrder(
      {
        // @ts-expect-error
        commit,
        // @ts-expect-error
        'state': { orders: stateOrders },
      },
      // @ts-expect-error
      { route, supplier, selectedOrder, userData },
    ) {
      useSharedStore().init()

      const oldRouteRef = selectedOrder.route

      const updateData = {
        route: route.reference,
        supplier,
        deliver: route.isSubDeliver
          ? route.subDeliver
          : route.deliver,
        lastUpdateByUser: userData.reference,
        lastUpdateTime: serverTimestamp(),
      }

      const onSuccess = () => {
        const routeId = route.reference.id
        const selectedOrderId = selectedOrder.reference.id
        const newOrder = {
          ...selectedOrder,
          ...updateData,
        }
        // @ts-expect-error
        const mapOrdersFilter = order => (order.reference.id === selectedOrderId
          ? null
          : order)
        let newOrdersList = {
          ...stateOrders,
        }
        if (stateOrders[routeId]) {
          newOrdersList = {
            ...newOrdersList,
            [routeId]: [
              ...(stateOrders[routeId] || []),
              newOrder,
            ],
          }
        }
        if (stateOrders[oldRouteRef.id]) {
          newOrdersList = {
            ...newOrdersList,
            [oldRouteRef.id]: [...(stateOrders[oldRouteRef.id] || []).filter(mapOrdersFilter)],
          }
        }
        commit(
          'SET_ORDERS',
          newOrdersList,
        )

        if (routeId !== oldRouteRef.id) {
          if (selectedOrder.completementStatus === Order.completementStatus.waitingFirmAccept) {
            updateDoc(
              route.reference,
              {
                pendingOrders: increment(1),
                lastUpdateByUser: userData.reference,
                lastUpdateTime: serverTimestamp(),
              },
            )
          }
          updateDoc(
            route.reference,
            {
              ordersCount: increment(1),
              lastUpdateByUser: userData.reference,
              lastUpdateTime: serverTimestamp(),
            },
          )

          if (selectedOrder.completementStatus === Order.completementStatus.waitingFirmAccept) {
            updateDoc(
              oldRouteRef,
              {
                pendingOrders: increment(-1),
                lastUpdateByUser: userData.reference,
                lastUpdateTime: serverTimestamp(),
              },
            )
          }

          updateDoc(
            oldRouteRef,
            {
              ordersCount: increment(-1),
              lastUpdateByUser: userData.reference,
              lastUpdateTime: serverTimestamp(),
            },
          )
        }
      }

      const onError = (error: unknown) => {
        console.error(error)
        useSharedStore().setError(error,
        )
        useSnackbarStore().showSnackbar(errorDefault(error),
        )
      }

      updateDoc(
        selectedOrder.reference,
        updateData,
      ).then(onSuccess).catch(onError).finally(onFinallySharedLoading)
    },
  },
  mutations: {
    RESET_STATE(_state: any) {
      Object.assign(
        _state,
        initialState(),
      )
    },

    // @ts-expect-error
    SET_ORDERS(state: any, orders) {
      state.orders = orders
    },

    // @ts-expect-error
    SAVE_UNSUBSCRIBE(state: any, unsubscribe) {
      state.unsubscribe = unsubscribe
    },

    GET_ORDERS_SUBSIDY_REQUEST(state: any) {
      state.ordersSubsidyLoading = true
      state.ordersSubsidyError = null
    },

    // @ts-expect-error
    GET_ORDERS_SUBSIDY_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersSubsidy || {} }

      state.ordersSubsidy = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersSubsidyLoading = false
    },

    // @ts-expect-error
    GET_ORDERS_SUBSIDY_ERROR(state: any, error) {
      state.ordersSubsidyLoading = false
      state.ordersSubsidyError = error
    },

    // @ts-expect-error
    SET_STATISTIC_ORDERS(state: any, orders) {
      state.statisticOrders = orders
    },

    // @ts-expect-error
    SET_LOADING_ORDER(state: any, { order, index, loading }) {
      // @ts-expect-error
      const updateOrderLoading = (orderItem) => {
        if (orderItem.reference.id === order.reference.id) {
          return {
            ...orderItem,
            orders: orderItem.orders.map(updateProduct(
              index,
              'loading',
              loading,
            )),
          }
        }
        return orderItem
      }

      state.orders = {
        ...state.orders,
        [getIdFromRef(order.route)]: state.orders[getIdFromRef(order.route)].map(updateOrderLoading),
      }
    },
    // @ts-expect-error
    SET_LOADING_WHOLE_ORDER(state: any, { order, loading }) {
      // @ts-expect-error
      const updateWholeOrderLoading = (orderItem) => {
        if (orderItem.reference.id === order.reference.id)
          orderItem.loading = loading

        return orderItem
      }

      state.orders = {
        ...state.orders,
        [getIdFromRef(order.route)]: state.orders[getIdFromRef(order.route)].map(updateWholeOrderLoading),
      }
    },
    // @ts-expect-error
    SET_STATUS_WHOLE_ORDER(state: any, { order, completementStatus }) {
      // @ts-expect-error
      const updateOrderStatus = (orderItem) => {
        if (orderItem.reference.id === order.reference.id)
          orderItem.completementStatus = completementStatus

        return orderItem
      }

      state.orders = {
        ...state.orders,
        [getIdFromRef(order.route)]: state.orders[getIdFromRef(order.route)].map(updateOrderStatus),
      }
    },

    GET_ORDERS_ONLINE_PEYMENT_REQUEST(state: any) {
      state.ordersOnlinePeymentLoading = true
      state.ordersOnlinePeymentError = null
    },

    // @ts-expect-error
    GET_ORDERS_ONLINE_PEYMENT_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersOnlinePeyment || {} }

      state.ordersOnlinePeyment = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersOnlinePeymentLoading = false
    },

    // @ts-expect-error
    GET_ORDERS_ONLINE_PEYMENT_ERROR(state: any, error) {
      state.ordersOnlinePeymentLoading = false
      state.ordersOnlinePeymentError = error
    },

    GET_ORDERS_SALES_PERCENTAGE_REQUEST(state: any) {
      state.ordersSalesPercentageLoading = true
      state.ordersSalesPercentageError = null
    },
    // @ts-expect-error
    GET_ORDERS_SALES_PERCENTAGE_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersSalesPercentage || {} }

      state.ordersSalesPercentage = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersSalesPercentageLoading = false
    },
    // @ts-expect-error
    GET_ORDERS_SALES_PERCENTAGE_ERRROR(state: any, error) {
      state.ordersSalesPercentageLoading = false
      state.ordersSalesPercentageError = error
    },

    GET_ORDERS_SALES_FIXED_REQUEST(state: any) {
      state.ordersFixedSalesLoading = true
      state.ordersFixedSalesError = null
    },
    // @ts-expect-error
    GET_ORDERS_SALES_FIXED_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersFixedSales || {} }

      state.ordersFixedSales = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersFixedSalesLoading = false
    },
    // @ts-expect-error
    GET_ORDERS_SALES_FIXED_ERRROR(state: any, error) {
      state.ordersFixedSalesLoading = false
      state.ordersFixedSalesError = error
    },

    GET_ORDERS_LOYALTY_PROGRAM_REQUEST(state: any) {
      state.ordersLoyaltyProgramLoading = true
      state.ordersLoyaltyProgramError = null
    },
    // @ts-expect-error
    GET_ORDERS_LOYALTY_PROGRAM_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersLoyaltyProgram || {} }

      state.ordersLoyaltyProgram = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersLoyaltyProgramLoading = false
    },
    // @ts-expect-error
    GET_ORDERS_LOYALTY_PROGRAM_ERRROR(state: any, error) {
      state.ordersLoyaltyProgramLoading = false
      state.ordersLoyaltyProgramError = error
    },

    GET_ORDERS_IS_NEW_USER_REQUEST(state: any) {
      state.ordersIsNewUserLoading = true
      state.ordersIsNewUserError = null
    },
    // @ts-expect-error
    GET_ORDERS_IS_NEW_USER_SUCCESS(state: any, { city, date, orders }) {
      const copy = { ...state.ordersIsNewUser || {} }

      state.ordersIsNewUser = {
        ...copy,
        [city]: {
          ...copy[city],
          [date]: orders,
        },
      }
      state.ordersIsNewUserLoading = false
    },
    // @ts-expect-error
    GET_ORDERS_IS_NEW_USER_ERRROR(state: any, error) {
      state.ordersIsNewUserLoading = false
      state.ordersIsNewUserError = error
    },

  },
}

export const useOrdersStore = defineStore('orders', () => {
  const state = reactive(initialState() as any)
  const actions = (ordersModule as any).actions
  const mutations = (ordersModule as any).mutations

  const commit = (mutationName: string, payload?: any) => {
    const mutation = mutations[mutationName]
    if (mutation)
      mutation(state, payload)
  }

  const runAction = (actionName: string, payload?: any) => {
    const action = actions[actionName]
    if (!action)
      return undefined

    return action({ commit, state }, payload)
  }

  const resetState = (payload?: any) => runAction('resetState', payload)
  const getOrders = (payload?: any) => runAction('getOrders', payload)
  const getOrdersSubsidy = (payload?: any) => runAction('getOrdersSubsidy', payload)
  const getOrdersOnlinePayment = (payload?: any) => runAction('getOrdersOnlinePayment', payload)
  const getOrdersFixedSales = (payload?: any) => runAction('getOrdersFixedSales', payload)
  const getOrdersSalesPercentage = (payload?: any) => runAction('getOrdersSalesPercentage', payload)
  const getOrdersLoyaltyProgram = (payload?: any) => runAction('getOrdersLoyaltyProgram', payload)
  const getOrdersIsNewUser = (payload?: any) => runAction('getOrdersIsNewUser', payload)
  const changeRouteOrdersCount = (payload?: any) => runAction('changeRouteOrdersCount', payload)
  const changeRoutePendingOrdersCount = (payload?: any) => runAction('changeRoutePendingOrdersCount', payload)
  const getStatisticOrders = (payload?: any) => runAction('getStatisticOrders', payload)
  const approveProducts = (payload?: any) => runAction('approveProducts', payload)
  const rejectProducts = (payload?: any) => runAction('rejectProducts', payload)
  const approveWholeOrder = (payload?: any) => runAction('approveWholeOrder', payload)
  const approveWholeOrderAndGetPayment = (payload?: any) => runAction('approveWholeOrderAndGetPayment', payload)
  const approveWholeOrderAndDeliveryTime = (payload?: any) => runAction('approveWholeOrderAndDeliveryTime', payload)
  const changeTime = (payload?: any) => runAction('changeTime', payload)
  const approveWholeOrderAndDeliveryTimeAndGetPayment = (payload?: any) => runAction('approveWholeOrderAndDeliveryTimeAndGetPayment', payload)
  const approveWholeOrderWithMessage = (payload?: any) => runAction('approveWholeOrderWithMessage', payload)
  const getPayment = (payload?: any) => runAction('getPayment', payload)
  const refundMoney = (payload?: any) => runAction('refundMoney', payload)
  const rejectWholeOrder = (payload?: any) => runAction('rejectWholeOrder', payload)
  const moveOrder = (payload?: any) => runAction('moveOrder', payload)

  return {
    ...toRefs(state),
    resetState,
    getOrders,
    getOrdersSubsidy,
    getOrdersOnlinePayment,
    getOrdersFixedSales,
    getOrdersSalesPercentage,
    getOrdersLoyaltyProgram,
    getOrdersIsNewUser,
    changeRouteOrdersCount,
    changeRoutePendingOrdersCount,
    getStatisticOrders,
    approveProducts,
    rejectProducts,
    approveWholeOrder,
    approveWholeOrderAndGetPayment,
    approveWholeOrderAndDeliveryTime,
    changeTime,
    approveWholeOrderAndDeliveryTimeAndGetPayment,
    approveWholeOrderWithMessage,
    getPayment,
    refundMoney,
    rejectWholeOrder,
    moveOrder,
  }
})

export default ordersModule
